class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results-container]');
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;

    this.abortController = null;
    this.debounceTimer = null;
    this.cache = new Map();

    if (!this.input || !this.resultsContainer || !this.predictiveSearchUrl) return;

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  onInput(event) {
    const term = event.target.value.trim();

    window.clearTimeout(this.debounceTimer);

    // Below this length the suggestions are mostly noise, and it keeps
    // requests well under Shopify's predictive search rate limit.
    if (term.length < 2) {
      this.close();
      return;
    }

    this.debounceTimer = window.setTimeout(() => this.search(term), 300);
  }

  onFocus() {
    const term = this.input.value.trim();
    if (term.length >= 2 && this.resultsContainer.innerHTML.trim() !== '') this.open();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (this.resultsContainer.hidden) return;

    const options = Array.from(this.resultsContainer.querySelectorAll('[role="option"]'));
    if (!options.length) return;

    event.preventDefault();

    const currentIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    let nextIndex;
    if (event.key === 'ArrowDown') {
      nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
    }

    options.forEach((option, index) => option.setAttribute('aria-selected', index === nextIndex ? 'true' : 'false'));
    options[nextIndex].querySelector('a, button')?.focus();
  }

  async search(term) {
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }

    // Cancel whatever's still in flight from earlier keystrokes so a slow
    // response for an old term can't land after (and overwrite) a newer,
    // faster one.
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.classList.add('predictive-search--loading');

    const url = `${this.predictiveSearchUrl}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,query&resources[limit]=6&resources[options][unavailable_products]=hide`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Predictive search request failed with status ${response.status}`);

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const section = html.getElementById('shopify-section-predictive-search');
      const markup = section ? section.innerHTML : '';

      this.cache.set(term, markup);
      this.render(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.close();
    } finally {
      this.classList.remove('predictive-search--loading');
    }
  }

  render(markup) {
    this.resultsContainer.innerHTML = markup;
    this.open();
  }

  open() {
    this.resultsContainer.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.input || !this.resultsContainer) return;
    this.resultsContainer.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('predictive-search', PredictiveSearch);
